// Card.RANKS - Constant with all SUIT properties
const RANKS = {
	2: { rank: '2', rankOrder: 1, value: 0, name: 'Two' },
	3: { rank: '3', rankOrder: 2, value: 0, name: 'Three' },
	4: { rank: '4', rankOrder: 3, value: 0, name: 'Four' },
	5: { rank: '5', rankOrder: 4, value: 0, name: 'Five' },
	6: { rank: '6', rankOrder: 5, value: 0, name: 'Six' },
	7: { rank: '7', rankOrder: 6, value: 0, name: 'Seven' },
	8: { rank: '8', rankOrder: 7, value: 0, name: 'Eight' },
	9: { rank: '9', rankOrder: 8, value: 0, name: 'Nine' },
	T: { rank: 'T', rankOrder: 9, value: 0, name: 'Ten' },
	J: { rank: 'J', rankOrder: 10, value: 1, name: 'Jack' },
	Q: { rank: 'Q', rankOrder: 11, value: 2, name: 'Queen' },
	K: { rank: 'K', rankOrder: 12, value: 3, name: 'King' },
	A: { rank: 'A', rankOrder: 13, value: 4, name: 'Ace' },
};

// Card.SUITS - Constant with all RANK properities
const SUITS = {
	C: {
		name: 'Clubs',
		symbol: '♣',
		order: 0,
		short: 'C',
		type: 'black',
		handOrder: 1,
	},
	D: {
		name: 'Diamonds',
		symbol: '♦',
		order: 1,
		short: 'D',
		type: 'red',
		handOrder: 2,
	},
	H: {
		name: 'Hearts',
		symbol: '❤',
		order: 2,
		short: 'H',
		type: 'red',
		handOrder: 4,
	},
	S: {
		name: 'Spades',
		symbol: '♠',
		order: 3,
		short: 'S',
		type: 'black',
		handOrder: 3,
	},
};

const STATES = ['undealt', 'dealt', 'inplay', 'played'];
/** Class representing a Card
 *
 * @example
 * //  Has two constructors:
 * // from a Rank and Suit string
 * let cardA = new Card("2", "S");
 * @example
 * // from a Rank+Suit string
 * let cardB = new Card("2S");
 *
 * @param  {String} rank - rank of card i.e.one if `23456789TJQKA`, this is overloaded, so that it will take a string of card name e.g. `2S` or `TH`
 * @param  {String}[suit = undefined] suit - suit of card i.e. one of  `CDHS`
 * @return {Card} card
 *
 */
class Card {
	// creates a new Card instance of rank and suit
	// `let card = new Card("1", "H");`
	constructor(rank, suit = undefined) {
		if (typeof suit == 'undefined') {
			if (rank.length != 2) {
				throw new Error('Missing suit or invalid string constructor');
			}
			suit = rank[1];
			rank = rank[0];
		}

		if (!(rank in RANKS)) {
			throw new Error('Invalid rank');
		}
		if (!(suit in SUITS)) {
			throw new Error('Invalid suit');
		}

		this.rank = RANKS[rank]; // TODO: Validate
		this.suit = SUITS[suit]; // TODO: Validate
		this.cardName = `${this.rank.name} of ${this.suit.name}`;
		this.shortName = `${this.rank.rank}${this.suit.short}`;

		this.value = this.rank.value;

		// This gives a simple way to compare cards =
		//  2H = (2 * 10) = 20 + (2) = 22
		//  2C = (2 * 10) = 20 + (0) = 20
		//  AC = (13 * 10) = 130 + (4) = 134
		//  AD = (13 * 10) = 130 + (1) = 131

		this.compareValue = this.rank.rankOrder * 10 + this.suit.order;
		this.rankSuitOrder = this.suit.handOrder * 100 + this.rank.rankOrder;
		this.state = 'undealt';
		this._player = null;

		this.score; // a placeholder for a DDS score
	}

	/**
	 * The name of the card, which is the same as `shortName`
	 * @return {String} card name such as `2C`, or `AH`
	 */
	get name() {
		return this.shortName;
	}

	/**
	 * Associate a player with this card
	 * @param  {Player} player the player who owns this card
	 */
	set player(player) {
		this._player = player;
	}

	/**
	 * Get the player associated with this card
	 * @return {Player} the player who owns this card
	 */
	get player() {
		return this._player;
	}

	/**
	 * The suit short name for this card
	 * @return {String}  short suit name for this card
	 */
	get suitName() {
		return this.suit.short;
	}

	/**
	 * The short name for the rank of this card
	 * @return {String} rank of this card, e.g. `2` or `A`, or `T`
	 */
	get rankName() {
		return this.rank.rank;
	}

	/**
	 * true, if this card is higher than the passed in card
	 * @param  {Card}  testCard card to test
	 * @return {boolean} false if passed in card higher
	 */
	isHigherThan(testCard) {
		return this.compareValue > testCard.compareValue;
	}

	/**
	 * short name of this card,
	 * @return {String}  e.g. "2H"
	 */
	toString() {
		return this.shortName;
	}

	/**
	 * Set the state of the card,
	 * @param  {String} newState - one of `"undealt", "dealt", "inplay", "played"`
	 */
	set state(newState) {
		if (!STATES.includes(newState)) {
			throw new Error('Invalid card state');
		}
		this._state = newState; // TODO: validate
	}

	/**
	 * the current state of the card
	 * @return {String} `"undealt", "dealt", "inplay", "played"`
	 */
	get state() {
		return this._state;
	}
}

/**
 * Suits - `N|S|E|W`  e.g. `C: { name: "Clubs", symbol: "♣", order: 0, short: "C" },`
 * @type {Object}
 */
Card.SUITS = SUITS;
/**
 * Card Ranks - e.g. 	K: { rank: "K", rankOrder: 12, value: 3, name: "King" },
 * @type {Object}
 */
Card.RANKS = RANKS;

// `Card.fromString` expects format "{rank}{suit}" - e.g. `let card = Card.fromString("2H")`
/**
 * Creates an instance of Card from a RANK SUIT string
 * @param  {String} cardString e.g. `"2H"`, `"QS"`
 * @return {Card}
 */
Card.fromString = (cardString) => {
	/** @lends Card */
	return new Card(cardString[0], cardString[1]); // TODO: validate
};

/**
 * compares two cards based on value
 * e.g. can be used to sort an array of cards: `cards.sort(Card.compare);`
 * @param  {Card} a
 * @param  {Card} b
 * @return {int} negative if card a is before card b
 */
Card.compare = (a, b) => {
	return a.compareValue - b.compareValue; //
};

Card.compareHandSort = (a, b) => {
	// Compare by rank then suit
	return a.rankSuitOrder - b.rankSuitOrder;
};

module.exports = Card;
